import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { cloneDeep } from 'lodash';

/**
 * PLEASE NOTE: This is not a singleton service, but a class to extend
 * when creating a unique stateful service
 */

@Injectable()
export abstract class BaseStatefulServiceClass<T> implements OnDestroy {
    public readonly $state: Observable<T>;
    private readonly _state: BehaviorSubject<T>;
    private _initialState: T;

    protected constructor() {
        this._initialState = null;
        this._state = new BehaviorSubject(null);
        this.$state = this._state.asObservable();
    }

    /**
     * Get the current state object
     * Choose to async pipe in templates wherever possible
     */
    public get state(): T {
        return this._state.value;
    }

    /**
     * Set the current state object
     *
     * @param stateObj
     */
    public set state(stateObj: T) {
        // break object reference
        const myCopy = cloneDeep(stateObj);
        this._state.next(myCopy);
    }

    /**
     * Initialize the state object with an initial value
     * Usually an empty version of the service object
     *
     * @param initialState
     */
    public init(initialState: T): void {
        this._initialState = cloneDeep(initialState);
        this._state.next(initialState);
    }

    /**
     * Check if a value of a certain property on the state object
     * is equal to a supplied value
     */
    public propCheck(propName: keyof T, val: unknown): boolean {
        return propName && (this.state || ({} as T))[propName] === val;
    }

    public resetToInitialState(): void {
        this._state.next(this._initialState);
    }

    ngOnDestroy() {
        this._state.complete();
    }
}
